import { useCallback, useEffect, useMemo, useState } from 'react'

const DEFAULT_AMOUNT = 50
export const MIN_TOP_UP_AMOUNT = 5
export const MAX_TOP_UP_AMOUNT = 999

export interface UseTopUpAmountReturn {
  selectedAmount: number | null
  customAmount: string
  handlePresetClick: (amount: number) => void
  handleCustomAmountChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  getCurrentAmount: () => number | null
  isValidAmount: boolean
  validationError: string | null
  reset: () => void
}

const getValidationError = (amount: number | null): string | null => {
  if (amount === null) return null
  if (amount < MIN_TOP_UP_AMOUNT) return `Minimum top-up amount is $${MIN_TOP_UP_AMOUNT}`
  if (amount > MAX_TOP_UP_AMOUNT) return `Maximum top-up amount is $${MAX_TOP_UP_AMOUNT}`
  return null
}

export const useTopUpAmount = (isOpen: boolean): UseTopUpAmountReturn => {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(DEFAULT_AMOUNT)
  const [customAmount, setCustomAmount] = useState('')

  useEffect(() => {
    if (!isOpen) {
      setSelectedAmount(DEFAULT_AMOUNT)
      setCustomAmount('')
    }
  }, [isOpen])

  const handlePresetClick = useCallback((amount: number): void => {
    setSelectedAmount(amount)
    setCustomAmount('')
  }, [])

  const handleCustomAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value
    if (value === '' || /^\d+$/.test(value)) {
      setCustomAmount(value)
      setSelectedAmount(null)
    }
  }, [])

  const getCurrentAmount = useCallback((): number | null => {
    if (customAmount) {
      return Number.parseInt(customAmount, 10)
    }
    return selectedAmount
  }, [customAmount, selectedAmount])

  const currentAmount = getCurrentAmount()
  const validationError = useMemo(() => getValidationError(currentAmount), [currentAmount])
  const isValidAmount = currentAmount !== null && validationError === null

  const reset = useCallback((): void => {
    setSelectedAmount(DEFAULT_AMOUNT)
    setCustomAmount('')
  }, [])

  return {
    selectedAmount,
    customAmount,
    handlePresetClick,
    handleCustomAmountChange,
    getCurrentAmount,
    isValidAmount,
    validationError,
    reset,
  }
}
